import { toBlob } from 'html-to-image';
import { fmtXp } from './gamification';

export const SHARE_WIDTH = 1200;
export const SHARE_HEIGHT = 627;

/** Nome do arquivo por tipo: studyspace-progresso.png, studyspace-nivel.png, studyspace-medalha.png. */
export const FILE_NAMES = { progress: 'progresso', level: 'nivel', medal: 'medalha' };
export const fileName = (kind) => `studyspace-${FILE_NAMES[kind] || kind}.png`;

// Descrição das medalhas em 1ª pessoa, para a imagem e o texto do post
const FIRST_PERSON = {
  first_lesson: 'concluí minha primeira aula',
  first_pass: 'passei no meu primeiro simulado',
  streak_7: 'estudei 7 dias seguidos',
  beat_the_clock: 'terminei um simulado rápido com 15 minutos de sobra',
  marathon: 'concluí um simulado completo de 80 questões',
  phase_done: 'concluí todas as aulas de uma fase',
  topic_master: 'alcancei ouro em um tópico',
  perfect_score: 'acertei 100% de um simulado rápido',
  error_hunter: 'revisei 50 erros do caderno',
  elephant_memory: 'revisei 500 flashcards',
  streak_30: 'estudei 30 dias seguidos',
  polymath: 'alcancei ouro em 5 tópicos',
};

const capitalize = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

export const medalDeed = (medal) =>
  FIRST_PERSON[medal?.key] || (medal?.description || '').replace(/^./, c => c.toLowerCase());

/** Frase da medalha na imagem: o feito em 1ª pessoa e quantos têm. */
export function medalSentence(medal) {
  const deed = capitalize(medalDeed(medal));
  const pct = medal?.holdersPct;
  if (!pct) return `${deed}.`;
  return medal.rarity === 'comum' ? `${deed}. ${pct}% dos estudantes têm.` : `${deed}. Só ${pct}% dos estudantes têm.`;
}

/** #softwarearchitecture a partir do nome do curso. */
export function courseHashtag(courseName) {
  const tag = (courseName || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .toLowerCase();
  return tag ? `#${tag} #estudos` : '#estudos';
}

const joinPt = (parts) => (parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`);

/** Texto padrão do post de cada tipo, com os dados reais. Trechos sem dado ficam de fora. */
export function defaultText(kind, { courseName, progress, level, medal }) {
  const tags = courseHashtag(courseName);
  const course = courseName || 'meus estudos';
  if (kind === 'level' && level) {
    return `Cheguei ao nível ${level.level} em ${course}! Foram ${fmtXp(level.xp)} XP entre aulas, simulados e revisões. ${tags}`;
  }
  if (kind === 'medal' && medal) {
    return `Conquistei a medalha "${medal.name}": ${medalDeed(medal)}. ${tags}`;
  }
  if (!progress) return tags;
  const parts = [
    `nível ${progress.level}`,
    `${progress.medals} ${progress.medals === 1 ? 'medalha' : 'medalhas'}`,
  ];
  if (progress.goldTopic) parts.push(`ouro em ${progress.goldTopic}`);
  const streak = progress.streak >= 2 ? ` São ${progress.streak} dias seguidos de estudo.` : '';
  return `Sigo avançando em ${course}: ${joinPt(parts)}.${streak} ${tags}`;
}

/** "15 de outubro de 2026". */
export const longDate = (d = new Date()) => d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });

export const initials = (name) =>
  (name || '').split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();

/** PNG 1200×627 do cartão, com as fontes já carregadas. */
export async function renderPng(node) {
  if (document.fonts?.ready) await document.fonts.ready;
  const blob = await toBlob(node, { width: SHARE_WIDTH, height: SHARE_HEIGHT, pixelRatio: 1, cacheBust: true });
  if (!blob) throw new Error('Não foi possível gerar a imagem.');
  return blob;
}

export function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * LinkedIn aceita o texto pela URL; a imagem é anexada à mão.
 * Teams só preenche a mensagem com um link público (href), que ainda não existe:
 * abre o Teams e o texto vai pela área de transferência.
 */
export const linkedInUrl = (text) => `https://www.linkedin.com/feed/?shareActive=true&text=${encodeURIComponent(text)}`;
export const TEAMS_URL = 'https://teams.microsoft.com/';

/** Celular com folha de compartilhamento nativa que aceita arquivos. */
export function canShareFile(file) {
  const touch = window.matchMedia?.('(pointer: coarse)').matches;
  try {
    return Boolean(touch && navigator.canShare?.({ files: [file] }));
  } catch {
    return false;
  }
}
